/**
 * MobileTimesheetList
 *
 * Mobile card-based replacement for the monthly DataTable in TimesheetView.
 * Shows each timesheet month as a touch-friendly card with month name,
 * date range, last updated, and status chip.
 * Supports search, status filter chips, and pagination.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTagClass } from "../../../app/utils/helpers";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

// Map PrimeReact Tag severity names to MUI Chip colors
const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

const PAGE_SIZE = 10;

/**
 * @param {Object} props
 * @param {Array}  props.timesheets - list of timesheet objects
 * @param {Function} props.onSelect - called with the month string when a card is tapped
 * @param {Array}  props.statuses - unique status values for filter chips
 */
export default function MobileTimesheetList({
  timesheets,
  onSelect,
  statuses,
}) {
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [page, setPage] = useState(1);

  // Filter data by search text and status
  const filteredData = useMemo(() => {
    let data = timesheets || [];

    // Status filter
    if (selectedStatus) {
      data = data.filter((t) => t.status === selectedStatus);
    }

    // Search filter
    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      data = data.filter((t) => {
        const searchStr = [
          t.month,
          t.status,
          t.from,
          t.to,
          t.updated_at,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }

    return data;
  }, [timesheets, searchText, selectedStatus]);

  // Reset page when filter changes
  React.useEffect(() => {
    setPage(1);
  }, [searchText, selectedStatus]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getStatusChipColor = (status) => {
    const severity = getTagClass(status);
    return severityToColor[severity] || "default";
  };

  const getStatusChipVariant = (status) => {
    const severity = getTagClass(status);
    return severity === "success" ? "filled" : "outlined";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD/MM/YYYY");
  };

  const handleStatusFilterClick = (status) => {
    setSelectedStatus((prev) => (prev === status ? null : status));
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: 0,
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {/* Status filter chips */}
      {statuses && statuses.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mb: 1.5,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Chip
            label={Localize("All")}
            size="small"
            onClick={() => setSelectedStatus(null)}
            variant={selectedStatus === null ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              height: 30,
              borderRadius: "10px",
              transition: TRANSITION_SMOOTH,
              flexShrink: 0,
              ...(selectedStatus === null && {
                bgcolor: "rgb(255,144,21)",
                color: "white",
                "&:hover": { bgcolor: "rgb(230,120,10)" },
              }),
            }}
          />
          {statuses.map((status) => (
            <Chip
              key={status}
              label={<Langs str={status} />}
              size="small"
              color={getStatusChipColor(status)}
              variant={selectedStatus === status ? "filled" : "outlined"}
              onClick={() => handleStatusFilterClick(status)}
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 30,
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                flexShrink: 0,
              }}
            />
          ))}
        </Box>
      )}

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 500,
          letterSpacing: "0.02em",
          opacity: 0.7,
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((timesheet, index) => (
          <Fade in key={timesheet.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow:
                  "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition:
                  "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelect(timesheet.month)}
                sx={{ p: 0, minHeight: 44 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Top row: month name + status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      {/* Calendar icon circle */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          bgcolor: "rgba(255,144,21,0.1)",
                          color: "rgb(255,144,21)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CalendarMonthOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          lineHeight: 1.3,
                          letterSpacing: "-0.01em",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textTransform: "capitalize",
                        }}
                      >
                        {timesheet.month}
                      </Typography>
                    </Box>

                    {/* Status chip */}
                    {timesheet.status && (
                      <Chip
                        label={<Langs str={timesheet.status} />}
                        size="small"
                        color={getStatusChipColor(timesheet.status)}
                        variant={getStatusChipVariant(timesheet.status)}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 24,
                          borderRadius: "8px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>

                  {/* Info rows */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      pl: 6.5,
                    }}
                  >
                    {/* Date range */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <DateRangeOutlinedIcon
                        sx={{
                          fontSize: "0.9rem",
                          color: "text.secondary",
                          opacity: 0.6,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.78rem" }}
                      >
                        {formatDate(timesheet.from)} - {formatDate(timesheet.to)}
                      </Typography>
                    </Box>

                    {/* Last updated */}
                    {timesheet.updated_at && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <UpdateOutlinedIcon
                          sx={{
                            fontSize: "0.9rem",
                            color: "text.secondary",
                            opacity: 0.6,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.78rem" }}
                        >
                          {timesheet.updated_at}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
